// src/data/suppliers.ts
// -------------------
// Repository for the `suppliers` table (an Unleashed mirror + a Zephyr overlay
// for the uniform "Authorisation for Purchase of Uniforms" document). Confines
// all `.from("suppliers")` access to the data layer; typed via getTypedClient().

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export interface SupplierRow {
  supplier_code: string;
  name: string;
  abn: string | null;
  kind: string;
  has_authority_doc: boolean;
  doc_name: string | null;
  logo_path: string | null;
  zephyr_account_number: string | null;
  account_terms: string | null;
  footer_message: string | null;
  payment_method: string; // supplier_invoices | shelter_reimbursed (db-50)
  document_type: string; // authorisation_to_purchase | reimbursement_approval | approved_list_only | school_locker_order | none (db-50)
  action_sheet_override: string | null; // NULL = derive from payment_method x document_type (db-50)
  dropdown_label: string | null; // wizard dropdown display text; NULL = use name (db-50)
  doc_template: string | null; // authorisation-PDF layout: NULL/supplier_account | invoice_zephyr | reimbursement (…15)
}

const SUPPLIER_COLS =
  "supplier_code, name, abn, kind, has_authority_doc, doc_name, logo_path, zephyr_account_number, account_terms, footer_message, payment_method, document_type, action_sheet_override, dropdown_label, doc_template";

// Read a single supplier row by its Unleashed supplier_code. Null on error / no
// client / not found.
export async function getSupplierByCode(code: string): Promise<SupplierRow | null> {
  const c = getTypedClient();
  if (!c || !code) return null;
  const { data, error } = await c
    .from("suppliers")
    .select(SUPPLIER_COLS)
    .eq("supplier_code", code)
    .maybeSingle();
  if (error) {
    logger.warn(`getSupplierByCode(${code}) failed:`, error.message);
    return null;
  }
  return (data as unknown as SupplierRow) ?? null;
}

// Resolve a request's uniform supplier row by NAME. Since db-46 the wizard stores the
// supplier NAME on children_uniforms.supplier (the dropdown is admin-driven), so we
// look it up by name among the uniform-flagged suppliers. Returns the row incl. `kind`
// (capture flow) + `has_authority_doc` (Print-Authorisation gating) so callers drive
// behaviour off the table, not a hardcoded name. Null on error / no client / not found.
export async function getUniformSupplier(name: string): Promise<SupplierRow | null> {
  const c = getTypedClient();
  if (!c || !name) return null;
  const { data, error } = await c
    .from("suppliers")
    .select(SUPPLIER_COLS)
    .eq("name", name)
    .eq("is_uniform_supplier", true)
    .limit(1);
  if (error) {
    logger.warn(`getUniformSupplier(${name}) failed:`, error.message);
    return null;
  }
  return data && data.length > 0 ? (data[0] as unknown as SupplierRow) : null;
}

// One option in the wizard's admin-driven uniform-supplier dropdown.
export interface DropdownSupplier {
  supplier_code: string;
  name: string;
  // Display text for the dropdown (dropdown_label ?? name). The request still
  // stores the stable `name`; `label` only changes what the caseworker reads.
  label: string;
  kind: string;
  has_authority_doc: boolean;
  payment_method: string;
  document_type: string;
  action_sheet_override: string | null;
  dropdown_label: string | null;
}

// The wizard's uniform-supplier dropdown (admin-driven since db-46): the
// uniform-flagged, active suppliers, name-ordered with "Other Supplier" pinned last
// (matches the previous UX). Each option carries `kind` + `has_authority_doc` so the
// wizard resolves the capture flow + doc gating from the row. [] empty, null on error.
export async function listUniformDropdownSuppliers(): Promise<DropdownSupplier[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("suppliers")
    .select(
      "supplier_code, name, kind, has_authority_doc, payment_method, document_type, action_sheet_override, dropdown_label"
    )
    .eq("is_uniform_supplier", true)
    .eq("deactivated", false)
    .order("name")
    .limit(SUPPLIERS_LIST_LIMIT);
  if (error) {
    logger.warn("listUniformDropdownSuppliers failed:", error.message);
    return null;
  }
  const raw = (data as unknown as Array<Omit<DropdownSupplier, "label">>) ?? [];
  // Compute the display label (dropdown_label ?? name) once per row.
  const rows: DropdownSupplier[] = raw.map((r) => ({
    ...r,
    label: r.dropdown_label ?? r.name,
  }));
  // "Other Supplier" pinned last, everything else alphabetical.
  return rows.sort((a, b) => {
    const ao = a.name === "Other Supplier" ? 1 : 0;
    const bo = b.name === "Other Supplier" ? 1 : 0;
    return ao !== bo ? ao - bo : a.name.localeCompare(b.name);
  });
}

// ── Admin console (supplier admin screen) ─────────────────────────────────────

// The columns the admin list + overlay edit panel need (identity + the Zephyr
// overlay). `id` is the surrogate key updates go through.
export interface AdminSupplierRow {
  id: number;
  supplier_code: string;
  name: string;
  abn: string | null;
  kind: string;
  has_authority_doc: boolean;
  so_suffix: string | null;
  is_uniform_supplier: boolean;
  unleashed_product_code: string | null;
  doc_name: string | null;
  logo_path: string | null;
  zephyr_account_number: string | null;
  account_terms: string | null;
  footer_message: string | null;
  payment_method: string;
  document_type: string;
  action_sheet_override: string | null;
  dropdown_label: string | null;
  doc_template: string | null; // authorisation-PDF layout (…15)
  deactivated: boolean;
}

// Typed as `string` (not a literal) so supabase-js doesn't deep-parse the select.
const ADMIN_SUPPLIER_COLS: string =
  "id, supplier_code, name, abn, kind, has_authority_doc, so_suffix, is_uniform_supplier, unleashed_product_code, doc_name, logo_path, " +
  "zephyr_account_number, account_terms, footer_message, payment_method, document_type, action_sheet_override, dropdown_label, doc_template, deactivated";

// Default (and admin-screen) page size. IMPORTANT: PostgREST silently caps an
// un-limited select at 1,000 rows — with ~1,432 suppliers a fetch-everything
// call truncates alphabetically (UMS/Weareco fell off the end when the admin
// screen filtered client-side). Filtering is therefore SERVER-side, with an
// explicit limit.
export const SUPPLIERS_LIST_LIMIT = 200;

export interface ListSuppliersOptions {
  // Only rows flagged is_uniform_supplier (served by the idx_suppliers_uniform
  // partial index) — the admin screen's default view.
  uniformOnly?: boolean;
  // Case-insensitive substring match on name OR supplier_code.
  search?: string;
  // Max rows returned (defaults to SUPPLIERS_LIST_LIMIT).
  limit?: number;
}

// List suppliers for the admin console, name-ordered, filters applied
// server-side. Deactivated rows are NOT filtered here — the admin console shows
// them (badge/toggle pattern). Returns null on error / no client.
export async function listSuppliers(
  opts: ListSuppliersOptions = {}
): Promise<AdminSupplierRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  let q = c.from("suppliers").select(ADMIN_SUPPLIER_COLS);
  if (opts.uniformOnly) q = q.eq("is_uniform_supplier", true);
  // Strip PostgREST .or() syntax characters from the user's search text so a
  // stray comma/paren/percent can't break (or inject into) the filter expression.
  const s = (opts.search ?? "")
    .replace(/[,()%]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (s) q = q.or(`name.ilike.%${s}%,supplier_code.ilike.%${s}%`);
  const { data, error } = await q.order("name").limit(opts.limit ?? SUPPLIERS_LIST_LIMIT);
  if (error) {
    logger.warn("listSuppliers failed:", error.message);
    return null;
  }
  return (data as unknown as AdminSupplierRow[]) ?? [];
}

type SupplierPatch = Database["public"]["Tables"]["suppliers"]["Update"];
type SupplierInsert = Database["public"]["Tables"]["suppliers"]["Insert"];
type WriteResult = { error: { message: string } | null };

// Update a supplier by surrogate id (the admin overlay edit — doc_name,
// zephyr_account_number, account_terms, footer_message, is_uniform_supplier,
// logo_path). Takes a loose patch object (admin form state) and casts to the
// generated Update shape, per the repo write convention. Logs on error and
// returns { error }; a missing client returns a null error (caller won't crash).
export async function updateSupplierById(
  id: number | undefined,
  patch: object
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("suppliers")
    .update(patch as SupplierPatch)
    .eq("id", id ?? 0);
  if (error) logger.warn("updateSupplierById failed:", error.message);
  return { error };
}

// Create a Zephyr-owned supplier row — the special/manual (non-Unleashed) options
// managed on the admin screen (School / P&C Shop, Shelter to Buy, Other, or any new
// manual entry). Takes a loose object (admin form state) cast to the generated Insert
// shape, per the repo write convention. Logs on error and returns { error }.
export async function createSupplier(row: object): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c.from("suppliers").insert(row as SupplierInsert);
  if (error) logger.warn("createSupplier failed:", error.message);
  return { error };
}

// Active uniform suppliers with their Unleashed product code (db-67) — the Order
// Builder's supplier-name → product_code lookup for uniform lines. null on error /
// no client.
export async function listUniformSupplierProductCodes(): Promise<Array<{
  name: string;
  code: string | null;
}> | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("suppliers")
    .select("name, unleashed_product_code")
    .eq("is_uniform_supplier", true)
    .eq("deactivated", false)
    .limit(500);
  if (error) {
    logger.warn("listUniformSupplierProductCodes failed:", error.message);
    return null;
  }
  const rows =
    (data as unknown as Array<{ name: string; unleashed_product_code: string | null }>) ?? [];
  return rows.map((r) => ({ name: r.name, code: r.unleashed_product_code }));
}

// Active uniform suppliers with everything the SO rules engine needs to route +
// price a uniform line: the supplier name (matches children_uniforms.supplier),
// its so_suffix / kind / payment_method (selectors) and its Unleashed product code
// (the line's product). null on error / no client.
export interface UniformSupplierMeta {
  name: string;
  so_suffix: string | null;
  kind: string | null;
  payment_method: string | null;
  unleashed_product_code: string | null;
}
export async function listUniformSupplierMeta(): Promise<UniformSupplierMeta[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("suppliers")
    .select("name, so_suffix, kind, payment_method, unleashed_product_code")
    .eq("is_uniform_supplier", true)
    .eq("deactivated", false)
    .limit(1000);
  if (error) {
    logger.warn("listUniformSupplierMeta failed:", error.message);
    return null;
  }
  return (data as unknown as UniformSupplierMeta[]) ?? [];
}
